import { keepPreviousData, useQuery } from "@tanstack/react-query";
import cx from "classnames";
import type { Hit } from "instantsearch.js";
import { type ReactNode, useState } from "react";
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  useInstantSearch,
  useSortBy,
} from "react-instantsearch";

import { useTranslation } from "@/core/i18n";
import { useFacet } from "@/hooks/useFacet";
import type { AlgoliaSearchResult } from "@/models/AlgoliaSearchResult";
import {
  buildAnalyticsTags,
  buildCatalogFilters,
  type CatalogHit,
  type CatalogVisibility,
  createListingHitsCache,
  getPrimarySearchIndexName,
  isCatalogHit,
  objectIdFilter,
  onSaleDiscounts,
  resolveSortIndexName,
  type SearchSurface,
  sortByCategoryOrder,
  sortByDiscount,
} from "@/utils/algoliaCatalog";
import { getAlgoliaSearchClient, getAlgoliaSearchIndexEnvName } from "@/utils/algoliaSearchClient";
import { convertListingTypeToProductType, type ListingTypeOptions } from "@/utils/productTypes";
import { Models } from "api/types";
import DownChevron from "atoms/icon/icons/chevron_down_small.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { ModalSelectorFilterList } from "organisms/selection-modals/modal-selector-filter-list/ModalSelectorFilterList";
import { type ListingSortOptions, sortOptions, typeOptions } from "./listingFilterOptions";
import { LoadMoreOnScroll } from "./LoadMoreOnScroll";
import { ShopFilterRail } from "./ShopFilterRail";
import { useListingFilterAndSort } from "./useListingFilterAndSort";

const HITS_PER_PAGE = 20;
const listingHitsCache = createListingHitsCache("shop-listings");
/** Algolia's ceiling for `hitsPerPage`. */
export const MAX_HITS_PER_PAGE = 1000;

export type ListingHitHelpers = {
  index: number;
};

interface Props {
  surface: SearchSurface;
  platformId: string;
  platformLocationId: string | undefined;
  visibility: CatalogVisibility;
  /** Listing types this page may show; one type is a hard filter, several become the type filter. */
  types: ListingTypeOptions[];
  areas: Models<"AreaResult">[];
  concerns: Models<"ConcernModel">[];
  /** Scope every result to this concern (the concern page). */
  concernId?: string;
  /** Scope every result to this custom category and render in its inclusion order. */
  categoryId?: string;
  /** The patient's discounts (`GET /shop/discounts`): what "On sale" lists and how it orders them. */
  shopDiscounts: Models<"ShopDiscountModel"> | undefined;
  /** Drop hits the page cannot render (a membership whose REST detail is missing, say). */
  hitFilter?: (hit: Hit<CatalogHit>) => boolean;
  /**
   * Page size, capped at `MAX_HITS_PER_PAGE`. A curated category passes its inclusion count so the
   * whole category arrives in one page and its inclusion order is complete before it renders.
   */
  hitsPerPage?: number;
  /**
   * The unlisted listings the patient unlocked by link, which the index never returns: filtered by the applied
   * refinements and "On sale" like hits, listed ahead of them on the featured order and merged into a sort's order.
   * A hit with the same id is dropped in their favour.
   */
  leadingItems?: LeadingItem[];
  children: (hit: Hit<CatalogHit>, helpers: ListingHitHelpers) => ReactNode;
}

export type LeadingItem = { listing: Models<"ListingModel">; node: ReactNode };

type SaleDiscounts = ReturnType<typeof onSaleDiscounts>;

const resolveSortForEnv = (sort: ListingSortOptions) =>
  resolveSortIndexName(sort, getAlgoliaSearchIndexEnvName());

/** How many of the patient's on-sale listings this page's scope holds: one hit-less, analytics-free count. */
const countOnSale = async (
  indexName: string,
  filters: string,
  facetFilters: string[][],
  analyticsTags: string[],
) => {
  const { results } = await getAlgoliaSearchClient().search<AlgoliaSearchResult>({
    requests: [
      {
        indexName,
        query: "",
        filters,
        facetFilters,
        hitsPerPage: 0,
        analytics: false,
        analyticsTags,
      },
    ],
  });
  const [result] = results;
  if (!result || !("nbHits" in result)) {
    throw new Error("Algolia on-sale count returned an unexpected response shape.");
  }
  return result.nbHits ?? 0;
};

const productTypesFor = (types: readonly ListingTypeOptions[]) => [
  ...new Set(types.map(convertListingTypeToProductType)),
];

/** The unlocked listings the applied refinements (any-of within a facet, as the refinement lists do) and "On sale" keep. */
const matchingLeadingItems = (
  leadingItems: readonly LeadingItem[],
  {
    types,
    filterAndSortState,
  }: {
    types: readonly ListingTypeOptions[];
    filterAndSortState: ReturnType<typeof useListingFilterAndSort>;
  },
  saleDiscounts: SaleDiscounts | null,
) => {
  const { concerns, areas, type } = filterAndSortState;
  return leadingItems.filter(
    ({ listing }) =>
      (concerns.length === 0 || concerns.some((id) => listing.concernIds.includes(id))) &&
      (areas.length === 0 || areas.some((id) => listing.areaIds.includes(id))) &&
      (types.length <= 1 || !type || listing.type === type) &&
      (!saleDiscounts || saleDiscounts.has(listing.id)),
  );
};

/** The key a sort replica ranks by, read from a hit or an unlocked listing; the featured order has none. */
const sortKey = (
  sort: ListingSortOptions,
): ((entry: { sellVolume: number; price: number; createdTimestamp: number }) => number) | null => {
  switch (sort) {
    case "bestsellers":
      return (entry) => -entry.sellVolume;
    case "priceLowToHigh":
      return (entry) => entry.price;
    case "priceHighToLow":
      return (entry) => -entry.price;
    case "recentlyAdded":
      return (entry) => -entry.createdTimestamp;
    case "featured":
    case "onsale":
      return null;
  }
};

type ListEntry =
  | { kind: "leading"; objectID: string; item: LeadingItem }
  | { kind: "hit"; objectID: string; hit: Hit<CatalogHit> };

type SortKey = NonNullable<ReturnType<typeof sortKey>>;

const entrySortKey = (entry: ListEntry, key: SortKey) =>
  entry.kind === "hit"
    ? key({
        sellVolume: entry.hit.sellVolume,
        price: entry.hit.minBasePrice,
        createdTimestamp: entry.hit.createdTimestamp,
      })
    : key({
        sellVolume: entry.item.listing.sellVolume,
        price: entry.item.listing.baseCost,
        createdTimestamp: new Date(entry.item.listing.created).valueOf() / 1000,
      });

/**
 * Slots each unlocked listing ahead of the first loaded hit it ranks before, keeping the replica's hit order. One
 * that ranks after every loaded hit waits for the last page, so a later page cannot land ahead of it.
 */
const mergeBySortKey = (
  leading: readonly ListEntry[],
  hits: readonly ListEntry[],
  key: SortKey,
  isLastPage: boolean,
): ListEntry[] => {
  const sorted = [...leading].sort((a, b) => entrySortKey(a, key) - entrySortKey(b, key));
  const merged: ListEntry[] = [];
  let next = 0;
  for (const hit of hits) {
    while (next < sorted.length && entrySortKey(sorted[next], key) < entrySortKey(hit, key)) {
      merged.push(sorted[next++]);
    }
    merged.push(hit);
  }
  return isLastPage ? [...merged, ...sorted.slice(next)] : merged;
};

const CustomHits = ({
  renderItem,
  categoryId,
  appliedSort,
  hitFilter,
  leadingItems,
  saleDiscounts,
}: {
  renderItem: Props["children"];
  categoryId: string | undefined;
  appliedSort: ListingSortOptions;
  hitFilter: Props["hitFilter"];
  leadingItems: LeadingItem[];
  /** Set while "On sale" is applied: hits are ordered by the size of their discount. */
  saleDiscounts: SaleDiscounts | null;
}) => {
  const { t } = useTranslation();
  const { status, results } = useInstantSearch();
  const { items, isLastPage, showMore } = useInfiniteHits<AlgoliaSearchResult>({
    cache: listingHitsCache,
  });

  if (status === "error") {
    return (
      <div className="my-3 flex items-center justify-center py-36">
        <div className="text-center text-gray-500">
          <p>{t("nextFilterAndSortListings.errorTitle")}</p>
          <p className="mt-2 text-sm">{t("nextFilterAndSortListings.errorSubtitle")}</p>
        </div>
      </div>
    );
  }

  // Before the first response `results` is InstantSearch's placeholder, which is not an empty result.
  const isPending = status === "loading" || status === "stalled" || results.__isArtificial;
  if (!items.length && !leadingItems.length && isPending) {
    return (
      <div className="my-1 flex items-center justify-center py-40">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-color border-b-transparent" />
      </div>
    );
  }

  const leadingIds = new Set(leadingItems.map(({ listing }) => listing.id));
  const visibleHits = items
    .filter((hit): hit is Hit<CatalogHit> => isCatalogHit(hit))
    .filter((hit) => !leadingIds.has(hit.objectID))
    .filter((hit) => hitFilter?.(hit) ?? true);
  const hitEntries = visibleHits.map(
    (hit): ListEntry => ({ kind: "hit", objectID: hit.objectID, hit }),
  );
  const leadingEntries = leadingItems.map(
    (item): ListEntry => ({ kind: "leading", objectID: item.listing.id, item }),
  );
  const key = sortKey(appliedSort);
  // "On sale" is scoped to the patient's on-sale listings, which all arrive in one page, and ordered as today's
  // shop orders it; a curated category keeps its inclusion order on the featured read ("On sale" with nothing on
  // sale falls back to it) and the sort replica's order otherwise. Unlocked listings lead the featured order and
  // are slotted into a sort replica's order by its key, never reordering the hits.
  const entries = saleDiscounts
    ? sortByDiscount([...leadingEntries, ...hitEntries], saleDiscounts)
    : categoryId && (appliedSort === "featured" || appliedSort === "onsale")
      ? [
          ...leadingEntries,
          ...sortByCategoryOrder(visibleHits, categoryId).map(
            (hit): ListEntry => ({ kind: "hit", objectID: hit.objectID, hit }),
          ),
        ]
      : key
        ? mergeBySortKey(leadingEntries, hitEntries, key, isLastPage)
        : [...leadingEntries, ...hitEntries];

  if (!entries.length) {
    return (
      <div className="mb-3 mt-2 flex items-center justify-center py-36">
        <div className="text-center text-gray-500">
          <p>{t("nextFilterAndSortListings.noResultsTitle")}</p>
          <p className="mt-2 text-sm">{t("nextFilterAndSortListings.noResultsSubtitle")}</p>
        </div>
      </div>
    );
  }

  let hitIndex = 0;
  return (
    <>
      {/* `grid-flow-dense` lets cards backfill the slots a full-row banner would otherwise leave ragged. */}
      <ol className="grid list-none grid-cols-1 gap-4 desk:grid-flow-row-dense desk:grid-cols-3">
        {entries.map((entry) => {
          if (entry.kind === "leading") {
            return (
              <li key={entry.objectID} className="flex flex-col gap-4">
                {entry.item.node}
              </li>
            );
          }
          const { hit } = entry;
          return (
            <li key={hit.objectID} className="flex flex-col gap-4 desk:contents">
              {renderItem(hit, { index: hitIndex++ })}
            </li>
          );
        })}
      </ol>
      {!isLastPage && <LoadMoreOnScroll key={items.length} onVisible={showMore} />}
    </>
  );
};

const AlgoliaFilterAndSortListings = ({
  types,
  areas,
  concerns,
  categoryId,
  hitFilter,
  leadingItems,
  filterAndSortState,
  hasOnSale,
  saleOrder,
  renderItem,
}: {
  types: ListingTypeOptions[];
  areas: Models<"AreaResult">[];
  concerns: Models<"ConcernModel">[];
  categoryId: string | undefined;
  hitFilter: Props["hitFilter"];
  leadingItems: LeadingItem[];
  filterAndSortState: ReturnType<typeof useListingFilterAndSort>;
  /** Whether this page's own scope holds anything on sale for the patient. */
  hasOnSale: boolean;
  /** Set while "On sale" is applied. */
  saleOrder: SaleDiscounts | null;
  renderItem: Props["children"];
}) => {
  const { t } = useTranslation();
  const { status, results } = useInstantSearch();
  // "On sale" is only offered while this page holds something on sale; otherwise it would list nothing.
  const availableSortOptions = Array.from(sortOptions).filter(
    ([id]) => id !== "onsale" || hasOnSale,
  );
  // "On sale" with nothing on sale here lists the featured order, so Featured is the sort shown as chosen.
  const shownSort = (sort: ListingSortOptions): ListingSortOptions =>
    sort === "onsale" && !hasOnSale ? "featured" : sort;

  const {
    facetValueToCountMap: productTypeFacets,
    resetFacet: resetProductType,
    setRefined: setRefinedProductType,
  } = useFacet({ attribute: "productType", limit: typeOptions.size });

  const {
    refine: refineBodyAreas,
    facetValueToCountMap: bodyAreasFacets,
    resetFacet: resetBodyAreas,
    setRefined: setRefinedBodyAreas,
  } = useFacet({ attribute: "bodyAreaIds", limit: Math.max(areas.length, 1) });

  const {
    refine: refineConcerns,
    facetValueToCountMap: concernsFacets,
    resetFacet: resetConcerns,
    setRefined: setRefinedConcerns,
  } = useFacet({ attribute: "concernIds", limit: Math.max(concerns.length, 1) });

  const { refine: refineSort } = useSortBy({
    items: availableSortOptions.map(([id, label]) => ({
      label: t(label),
      value: resolveSortForEnv(id),
    })),
  });

  const [activeModal, setActiveModal] = useState<"concern" | "area" | "type" | "sort" | null>(null);

  const filterLabel = (value: string, count: number) =>
    count <= 1 ? value : `${value} +${count - 1}`;

  const productTypeOf = (type: ListingTypeOptions) => convertListingTypeToProductType(type);
  const typeCount = (type: ListingTypeOptions) => productTypeFacets.get(productTypeOf(type)) ?? 0;

  const isSearching = status === "loading" || status === "stalled";
  const visibleLeadingItems = matchingLeadingItems(
    leadingItems,
    { types, filterAndSortState },
    saleOrder,
  );
  const showTreatmentTypeFilter = types.length > 1;
  // Once counts have arrived, a type with nothing in this page's scope is not offered; the applied one stays.
  const offeredTypes = results.__isArtificial
    ? types
    : types.filter((id) => typeCount(id) > 0 || filterAndSortState.type === id);
  const showConcernFilter = concerns.length > 0 && concernsFacets.size > 0;
  const showAreaFilter = areas.length > 0 && bodyAreasFacets.size > 0;

  const applySort = (sort: ListingSortOptions) => {
    filterAndSortState.applySort(sort);
    refineSort(resolveSortForEnv(sort));
  };

  const resetAll = () => {
    filterAndSortState.reset();
    resetProductType();
    resetConcerns();
    resetBodyAreas();
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col px-6 py-4 desk:px-8 desk:py-8">
      {/* Phone and small-tablet controls. From `lg` the same choices are always
          on screen in the rail below, so these collapse away. */}
      <div
        className={cx(
          "flex items-center overflow-x-auto desklg:hidden",
          (showTreatmentTypeFilter || showConcernFilter) && "justify-between gap-2",
        )}
      >
        <div className="flex flex-row items-center justify-start">
          {(showTreatmentTypeFilter || showConcernFilter) && (
            <div className="text-body2 text-primary">{t("filterAndSortListings.filterLabel")}</div>
          )}
          {showTreatmentTypeFilter && (
            <TextButton
              endIconSVG={DownChevron}
              onClick={() => {
                setActiveModal("type");
                filterAndSortState.saveCheckpoints();
              }}
            >
              <div className="text-bold2 text-primary">
                {filterAndSortState.type
                  ? t(typeOptions.get(filterAndSortState.type)!)
                  : t("filterAndSortListings.serviceType")}
              </div>
            </TextButton>
          )}
          {showConcernFilter && (
            <TextButton
              endIconSVG={DownChevron}
              onClick={() => {
                setActiveModal("concern");
                filterAndSortState.saveCheckpoints();
              }}
            >
              <div className="text-bold2 text-primary">
                {filterLabel(
                  concerns.find(({ id }) => id === filterAndSortState.concerns[0])?.name ??
                    t("filterAndSortListings.concernFallback"),
                  filterAndSortState.concerns.length,
                )}
              </div>
            </TextButton>
          )}
          {showAreaFilter && (
            <TextButton
              endIconSVG={DownChevron}
              onClick={() => {
                setActiveModal("area");
                filterAndSortState.saveCheckpoints();
              }}
            >
              <div className="text-bold2 text-primary">
                {filterLabel(
                  areas.find(({ id }) => id === filterAndSortState.areas[0])?.name ??
                    t("filterAndSortListings.areaFallback"),
                  filterAndSortState.areas.length,
                )}
              </div>
            </TextButton>
          )}
        </div>
        <div className="flex flex-row items-center">
          <div className="text-body2 text-primary">{t("filterAndSortListings.sortLabel")}</div>
          <TextButton
            onClick={() => {
              setActiveModal("sort");
              filterAndSortState.saveCheckpoints();
            }}
          >
            <div className="whitespace-nowrap text-bold2 text-primary">
              {t(sortOptions.get(shownSort(filterAndSortState.appliedSort))!)}
            </div>
          </TextButton>
        </div>
      </div>

      <div className="desklg:flex desklg:items-start desklg:gap-8">
        <aside className="hidden desklg:block desklg:w-56 desklg:shrink-0 deskxl:w-64">
          <ShopFilterRail
            sort={{
              title: t("filterAndSortListings.sortBy"),
              selectedId: shownSort(filterAndSortState.appliedSort),
              options: availableSortOptions.map(([id, label]) => ({ id, label: t(label) })),
              onSelect: applySort,
            }}
            groups={[
              {
                title: t("filterAndSortListings.filterByServiceType"),
                options: showTreatmentTypeFilter
                  ? offeredTypes.map((id) => ({
                      id,
                      label: t(typeOptions.get(id)!),
                      count: typeCount(id),
                    }))
                  : [],
                selectedIds: filterAndSortState.type ? [filterAndSortState.type] : [],
                // Service type is single-select: picking the active one clears it. The rail has no
                // "apply" step, so every toggle is written to the URL at once.
                onToggle: (id) => {
                  const next = filterAndSortState.type === id ? null : (id as ListingTypeOptions);
                  filterAndSortState.applyType(next);
                  setRefinedProductType(next ? [productTypeOf(next)] : []);
                },
              },
              {
                title: t("filterAndSortListings.filterByConcern"),
                // A facet with no name is not something a patient can act on,
                // so it is left out rather than rendered as a blank row.
                options: showConcernFilter
                  ? concerns
                      .filter((item): item is typeof item & { name: string } => !!item.name)
                      .map(({ id, name }) => ({
                        id,
                        label: name,
                        count: concernsFacets.get(id) ?? 0,
                      }))
                  : [],
                selectedIds: filterAndSortState.concerns,
                onToggle: (id) => {
                  filterAndSortState.applyConcern(id);
                  refineConcerns(id);
                },
              },
              {
                title: t("filterAndSortListings.filterByArea"),
                options: showAreaFilter
                  ? areas
                      .filter((item): item is typeof item & { name: string } => !!item.name)
                      .map(({ id, name }) => ({
                        id,
                        label: name,
                        count: bodyAreasFacets.get(id) ?? 0,
                      }))
                  : [],
                selectedIds: filterAndSortState.areas,
                onToggle: (id) => {
                  filterAndSortState.applyArea(id);
                  refineBodyAreas(id);
                },
              },
            ]}
            onReset={resetAll}
          />
        </aside>

        <div className="desklg:min-w-0 desklg:flex-1">
          <CustomHits
            renderItem={renderItem}
            categoryId={categoryId}
            appliedSort={filterAndSortState.appliedSort}
            hitFilter={hitFilter}
            leadingItems={visibleLeadingItems}
            saleDiscounts={saleOrder}
          />
        </div>
      </div>

      {/* Modals rendered outside the scrollable/relative containers to avoid iOS stacking context issues */}
      {activeModal === "type" && (
        <ModalSelectorFilterList
          open
          onClose={() => {
            setActiveModal(null);
            filterAndSortState.restore();
            const { type } = filterAndSortState.checkpoints;
            setRefinedProductType(type ? [productTypeOf(type)] : []);
          }}
          title={t("filterAndSortListings.filterByServiceType")}
          type="filter"
          items={offeredTypes.map((id) => ({
            id,
            title: t(typeOptions.get(id)!),
            disabled: isSearching || typeCount(id) === 0,
          }))}
          checkedItems={filterAndSortState.type ?? []}
          onChange={(id) => {
            const next = id === filterAndSortState.type ? null : (id as ListingTypeOptions);
            filterAndSortState.setType(next);
            setRefinedProductType(next ? [productTypeOf(next)] : []);
          }}
          firstButtonLabel={t("common.apply")}
          onFirstButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setSearchParams();
          }}
          firstButtonDisabled={filterAndSortState.type == null}
          secondButtonLabel={t("filterAndSortListings.resetFilter")}
          onSecondButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setType(null);
            resetProductType();
          }}
        />
      )}
      {activeModal === "concern" && (
        <ModalSelectorFilterList
          open
          onClose={() => {
            setActiveModal(null);
            filterAndSortState.restore();
            setRefinedConcerns(filterAndSortState.checkpoints.concerns);
          }}
          title={t("filterAndSortListings.filterByConcern")}
          type="filter"
          items={concerns
            .filter(({ id }) => concernsFacets.has(id))
            .map(({ id, name }) => ({
              id,
              title: name ?? "",
              disabled: isSearching,
            }))}
          checkedItems={filterAndSortState.concerns}
          onChange={(concernId) => {
            filterAndSortState.setConcerns(concernId);
            refineConcerns(concernId);
          }}
          firstButtonLabel={t("common.apply")}
          onFirstButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setSearchParams();
          }}
          firstButtonDisabled={!filterAndSortState.concerns.length}
          secondButtonLabel={t("filterAndSortListings.resetFilter")}
          onSecondButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.clearConcerns();
            resetConcerns();
          }}
        />
      )}
      {activeModal === "area" && (
        <ModalSelectorFilterList
          open
          onClose={() => {
            setActiveModal(null);
            filterAndSortState.restore();
            setRefinedBodyAreas(filterAndSortState.checkpoints.areas);
          }}
          title={t("filterAndSortListings.filterByArea")}
          type="filter"
          items={areas
            .filter(({ id }) => bodyAreasFacets.has(id))
            .map(({ id, name }) => ({
              id,
              title: name ?? "",
              disabled: isSearching,
            }))}
          checkedItems={filterAndSortState.areas}
          onChange={(areaId) => {
            filterAndSortState.setAreas(areaId);
            refineBodyAreas(areaId);
          }}
          firstButtonLabel={t("common.apply")}
          onFirstButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setSearchParams();
          }}
          firstButtonDisabled={!filterAndSortState.areas.length}
          secondButtonLabel={t("filterAndSortListings.resetFilter")}
          onSecondButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.clearAreas();
            resetBodyAreas();
          }}
        />
      )}
      {activeModal === "sort" && (
        <ModalSelectorFilterList
          open
          onClose={() => {
            setActiveModal(null);
            filterAndSortState.restore();
          }}
          title={t("filterAndSortListings.sortBy")}
          type="sort"
          items={availableSortOptions.map(([id, title]) => ({ id, title: t(title) }))}
          checkedItems={shownSort(filterAndSortState.sort)}
          onChange={(id) => filterAndSortState.sortBy(id as ListingSortOptions)}
          firstButtonLabel={t("common.apply")}
          onFirstButtonClick={() => {
            setActiveModal(null);
            applySort(filterAndSortState.sort);
          }}
        />
      )}
    </div>
  );
};

/**
 * Filterable, sortable shop list read from the Algolia index. Every request carries the practice,
 * visibility and location filter plus `analyticsTags` naming the platform and the surface. "On sale"
 * lists the patient's on-sale listings from `GET /shop/discounts`, never a promotion on the record.
 */
export const NextFilterAndSortListings = ({
  surface,
  platformId,
  platformLocationId,
  visibility,
  types,
  areas,
  concerns,
  concernId,
  categoryId,
  hitFilter,
  shopDiscounts,
  hitsPerPage = HITS_PER_PAGE,
  leadingItems = [],
  children: renderItem,
}: Props) => {
  const filterAndSortState = useListingFilterAndSort(types.length === 1 ? types[0] : null);
  // The availability window is read once per mount: a filter that moved with the clock would re-run the search
  // and drop every page "Load more" had added.
  const [nowMs] = useState(Date.now);
  const indexName = getPrimarySearchIndexName();
  const saleDiscounts = onSaleDiscounts(shopDiscounts);
  const sortIndexName = resolveSortForEnv(filterAndSortState.appliedSort);

  const catalogFilters = buildCatalogFilters(
    {
      platformId,
      platformLocationId,
      visibility,
      productTypes: productTypesFor(types),
      concernId,
      categoryId,
    },
    nowMs,
  );
  const saleIds = [...saleDiscounts.keys()].slice(0, MAX_HITS_PER_PAGE);
  const onSaleFilters =
    saleIds.length > 0 ? `${catalogFilters} AND ${objectIdFilter(saleIds)}` : null;
  const onSaleFacetFilters = [
    filterAndSortState.concerns.map((id) => `concernIds:${id}`),
    filterAndSortState.areas.map((id) => `bodyAreaIds:${id}`),
    types.length > 1 && filterAndSortState.type
      ? [`productType:${convertListingTypeToProductType(filterAndSortState.type)}`]
      : [],
  ].filter((group) => group.length > 0);
  const { data: onSaleCount } = useQuery({
    queryKey: ["algolia/on-sale-count", indexName, onSaleFilters, onSaleFacetFilters],

    queryFn: () =>
      countOnSale(
        indexName,
        onSaleFilters!,
        onSaleFacetFilters,
        buildAnalyticsTags(platformId, surface),
      ),

    enabled: !!onSaleFilters,
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
  });
  const hasOnSale = !!onSaleFilters && (onSaleCount ?? 0) > 0;
  // "On sale" with nothing on sale on this page (or before that is known) lists the featured order.
  const isOnSale = filterAndSortState.appliedSort === "onsale" && hasOnSale;
  const filters = isOnSale ? onSaleFilters! : catalogFilters;
  const effectiveHitsPerPage = isOnSale ? saleIds.length : hitsPerPage;

  return (
    <InstantSearch
      searchClient={getAlgoliaSearchClient()}
      indexName={indexName}
      initialUiState={{
        [indexName]: {
          sortBy: sortIndexName,
          refinementList: {
            concernIds: filterAndSortState.concerns,
            bodyAreaIds: filterAndSortState.areas,
            productType:
              types.length > 1 && filterAndSortState.type
                ? [convertListingTypeToProductType(filterAndSortState.type)]
                : [],
          },
        },
      }}
    >
      <Configure
        hitsPerPage={Math.min(Math.max(effectiveHitsPerPage, 1), MAX_HITS_PER_PAGE)}
        filters={filters}
        analyticsTags={buildAnalyticsTags(platformId, surface)}
      />
      <AlgoliaFilterAndSortListings
        types={types}
        areas={areas}
        concerns={concerns}
        categoryId={categoryId}
        hitFilter={hitFilter}
        leadingItems={leadingItems}
        filterAndSortState={filterAndSortState}
        hasOnSale={hasOnSale}
        saleOrder={isOnSale ? saleDiscounts : null}
        renderItem={renderItem}
      />
    </InstantSearch>
  );
};
